import {
	Component,
	Input,
	OnInit,
	ChangeDetectionStrategy,
} from "@angular/core"
import { COMMON_IMPORTS } from "@app/common-imports"
import {
	AutoCompleteKeyUpObj,
	ConfigDefinitions,
	daysOfWeek,
	TimeAllocationForm,
} from "@app/definitions/types"
import { GenAutocompleteComponent } from "@app/gen-inputs/gen-autocomplete/gen-autocomplete.component"
import { Modalable } from "@app/Modalable"
import { LangValPipe } from "@app/utils/lang-val.pipe"
import { TimePickerComponent } from "@app/utils/time-picker/time-picker.component"
import { BtnExtComponent, CancelBtnComponent } from "@app/utils/utils.component"

@Component({
	selector: "app-time-allocation-form",
	imports: [
		COMMON_IMPORTS,
		LangValPipe,
		GenAutocompleteComponent,
		TimePickerComponent,
		CancelBtnComponent,
		BtnExtComponent,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./time-allocation-form.component.html",
})
export class TimeAllocationFormComponent extends Modalable {
	timeAllocationForm = new TimeAllocationForm() //the object that holds the field values (and is saved)
	errors: any = {} //will hold errors for the fields in the form
	isFormSubmittable: boolean = false //can submit (controls disabled in the submit button)
	validationFields: any
	daysOfWeek = daysOfWeek
	@Input() isUpdateMode: boolean = false
	@Input() events: any[] = []
	@Input() insertObj: any = {}
	objNames: any = {}
	isSaving: string = ""
	tableName: string = "time_allocations"
	dayName: string = ""

	ngOnInit() {
		this.tableName = this.permsService.owner_has_users
			? "time_allocations"
			: "solo_time_allocations"

		this.validationFields = {
			//field validations, control displaying field errors and ability to submit the form
			time: { not_empty: true },
			time_to: { not_empty: true },
		}
		if (this.permsService?.owner_has_users) {
			this.validationFields = {
				...this.validationFields,
				user_id: { is_legit_fuzzy_value: null },
				location_id: { is_legit_fuzzy_value_or_empty: null },
			}
		}

		Object.keys(this.insertObj).forEach((key) => {
			this.timeAllocationForm[key] = this.insertObj[key]
		})
		this.dayName = daysOfWeek[this.timeAllocationForm.day]

		if (this.isUpdateMode) {
			this.objNames.user_id = this.store.searchSiteDataItem(
				this.timeAllocationForm.user_id,
				["sub_users"]
			)?.name
			this.objNames.location_id = this.store.searchSiteDataItem(
				this.timeAllocationForm.location_id,
				["locations"]
			)?.name
			this.validateEntireForm()
		} else if (this.timeAllocationForm.user_id) {
			this.objNames.user_id = this.store.searchSiteDataItem(
				this.timeAllocationForm.user_id,
				["sub_users"]
			)?.name
			this.validateEntireForm()
		}
	}

	validateEntireForm() {
		//validate all fields and detrmine if can be submitted (or has errors)
		this.isFormSubmittable = this.validator.validateEntireForm(
			this.timeAllocationForm,
			this.errors,
			this.validationFields
		)

		//time > time_to
		const time = this.store.timeToMoment(this.timeAllocationForm.time)
		const time_to = this.store.timeToMoment(this.timeAllocationForm.time_to)
		if (time.isSameOrAfter(time_to)) {
			this.isFormSubmittable = false
			this.errors.time_to = this.lang.getVal("error_in_time_range")
		}

		//overlapping hours on same day for same user
		const eventsThisDayThisUser = this.events.filter(
			(ev) =>
				ev.day == this.timeAllocationForm.day &&
				ev.user_id == this.timeAllocationForm.user_id &&
				ev.id != this.timeAllocationForm.id
		)
		this.findConflictsWithEvents(eventsThisDayThisUser)

		const locations = this.store.getSiteDataTable("locations")
		const nonConflictingLocationIds = locations
			.filter((loc) => loc.is_conflicting == "no")
			.map((loc) => loc.id)

		//location is already is use by a different user, same day SOME hours
		//conflicting: must be location (not empty) that ISN'T is_conflicting="no"
		let eventsThisDayThisLocation = this.events.filter(
			(ev) =>
				ev.day == this.timeAllocationForm.day &&
				ev.id != this.timeAllocationForm.id
		)

		if (this.permsService.owner_has_users) {
			eventsThisDayThisLocation = this.events.filter(
				(ev) =>
					ev.day == this.timeAllocationForm.day &&
					ev.location_id &&
					ev.location_id == this.timeAllocationForm.location_id &&
					!nonConflictingLocationIds.includes(ev.location_id) &&
					ev.id != this.timeAllocationForm.id
			)
		}

		this.findConflictsWithEvents(eventsThisDayThisLocation)
	}

	findConflictsWithEvents(eventsArr: any[]) {
		const errorText = this.permsService.owner_has_users
			? "room_is_taken"
			: "time_is_taken"

		for (let ev of eventsArr) {
			const time = this.store.timeToMoment(this.timeAllocationForm.time)
			const time_to = this.store.timeToMoment(this.timeAllocationForm.time_to)
			const evTime = this.store.timeToMoment(ev.time)
			const evTime_to = this.store.timeToMoment(ev.time_to)
			if (time.isBetween(evTime, evTime_to)) {
				this.isFormSubmittable = false
				this.errors.time = this.lang.getVal(errorText)
				break
			}
			if (time_to.isBetween(evTime, evTime_to)) {
				this.isFormSubmittable = false
				this.errors.time_to = this.lang.getVal(errorText)
				break
			}
			if (evTime.isBetween(time, time_to)) {
				this.isFormSubmittable = false
				this.errors.time = this.lang.getVal(errorText)
				break
			}
			if (evTime_to.isBetween(time, time_to)) {
				this.isFormSubmittable = false
				this.errors.time = this.lang.getVal(errorText)
				break
			}
		}
	}

	autocompleteKeyUp(fieldName: string, obj: AutoCompleteKeyUpObj) {
		// if (this.isUpdateMode) { return; }
		if (!obj.isIdenticalToPreviousValue) {
			this.timeAllocationForm[fieldName] = null
			this.validateEntireForm()
		}
	}
	chooseAutocomplete(fieldName: string, obj: any = null) {
		this.timeAllocationForm[fieldName] = obj?.id || 0
		this.validateEntireForm()
	}

	noErrorsAndMes(obj: any) {
		delete obj.errors
		delete obj.message
	}

	async submit() {
		if (!this.isFormSubmittable) {
			return
		} //has errors - exit
		this.isSaving = "save"

		if (this.isUpdateMode) {
			const res: any = await this.apiService.post("update_gen_item", {
				tableName: this.tableName,
				...this.timeAllocationForm,
			})
			if (res.id) {
				this.noErrorsAndMes(res)
				this.store.siteDataUpdateRow(this.tableName, res)
				this.onClose.emit(res)
			}
		} else {
			const res: any = await this.apiService.save_gen_item(
				this.tableName,
				this.timeAllocationForm
			)
			this.isSaving = ""
			if (res.id) {
				this.noErrorsAndMes(res)
				this.store.addGenItemRow(this.tableName, res)
				this.onClose.emit(res)
			}
		}
	}

	async delete() {
		this.isSaving = "delete"
		const res: any = await this.apiService.delete_gen_item(
			this.tableName,
			this.timeAllocationForm.id
		)
		this.isSaving = ""
		if (res.success) {
			this.store.delGenItemRow(this.tableName, this.timeAllocationForm.id)
			this.onClose.emit({ deletedId: this.timeAllocationForm.id })
		}
	}
}
